import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const IconPause = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);
const IconRestart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
  </svg>
);
const IconRewind = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
  </svg>
);
const IconFastForward = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
  </svg>
);

// ── Modal ─────────────────────────────────────────────────────────────────────
const VideoAudioPlayer = ({
  show,
  onClose,
  selectedRecording,
  onDelete,
  deleting = false,
  deleteError,
  onSaveDescription,
  savingDescription = false,
  descriptionError,
}) => {
  const audioRef = useRef(null); // Audio() instance — audio mode only
  const videoRef = useRef(null); // <video> DOM ref — video mode only
  const intervalRef = useRef(null);

  const isVideo = selectedRecording?.media_type === "video";
  const fileUrl = selectedRecording?.file_url ?? null;

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState("");

  useEffect(() => {
    setDescription(selectedRecording?.description || "");
  }, [selectedRecording]);

  // ── Get the active media element (video el or Audio instance) ─────────────
  const getMedia = () => (isVideo ? videoRef.current : audioRef.current);

  // ── Tick interval ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        const m = getMedia();
        if (m) setCurrentTime(m.currentTime);
      }, 250);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, isVideo]);

  // ── Audio mode: create Audio() when url changes ────────────────────────────
  useEffect(() => {
    if (!fileUrl || isVideo) return;

    const audio = new Audio(fileUrl);
    audio.playbackRate = playbackRate;
    audioRef.current = audio;

    const onLoaded = () => setDuration(audio.duration);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(audio.duration);
      clearInterval(intervalRef.current);
    };
    const onError = () => setError("Playback error.");

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      clearInterval(intervalRef.current);
      audioRef.current = null;
    };
  }, [fileUrl, isVideo]);

  // ── Video mode: wire events to <video> ref after modal mounts ─────────────
  // videoRef is set via callback ref below; we just need to handle playbackRate changes
  useEffect(() => {
    if (!isVideo || !videoRef.current) return;
    videoRef.current.playbackRate = playbackRate;
  }, [playbackRate, isVideo]);

  // ── Video ref callback — attach event listeners once el is in DOM ──────────
  const setVideoRef = useCallback((el) => {
    if (!el) return;
    videoRef.current = el;

    el.addEventListener("loadedmetadata", () => setDuration(el.duration));
    el.addEventListener("ended", () => {
      setPlaying(false);
      setCurrentTime(el.duration);
      clearInterval(intervalRef.current);
    });
    el.addEventListener("error", () => setError("Playback error."));
  }, []);

  // ── Controls ───────────────────────────────────────────────────────────────
  const togglePlay = () => {
    const m = getMedia();
    if (!m) return;
    if (playing) {
      m.pause();
      setPlaying(false);
    } else {
      m.play();
      setPlaying(true);
    }
  };

  const restart = () => {
    const m = getMedia();
    if (!m) return;
    m.currentTime = 0;
    setCurrentTime(0);
    m.play();
    setPlaying(true);
  };

  const skip = (secs) => {
    const m = getMedia();
    if (!m) return;
    const next = Math.min(Math.max(0, m.currentTime + secs), duration);
    m.currentTime = next;
    setCurrentTime(next);
  };

  const handleSeekChange = (e) => {
    const val = parseFloat(e.target.value);
    setCurrentTime(val);
    const m = getMedia();
    if (m) m.currentTime = val;
  };

  const handleRateChange = (rate) => {
    setPlaybackRate(rate);
    const m = getMedia();
    if (m) m.playbackRate = rate;
  };

  const handleDownload = async () => {
    if (!fileUrl) return;
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = selectedRecording?.original_filename || "recording";
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleClose = () => {
    if (deleting || savingDescription) return;

    const m = getMedia();
    if (m) {
      if (isVideo) {
        m.pause();
        m.currentTime = 0;
      } else {
        m.pause();
        audioRef.current = null;
      }
    }
    clearInterval(intervalRef.current);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    onClose();
  };

  const handleSaveDescription = async () => {
    try {
      await onSaveDescription?.(description);
    } catch {
      // The parent displays the API error and keeps the popup open for another attempt.
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const rates = [0.5, 1, 1.5, 2];

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="md"
      dialogClassName="modal-dialog modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
    >
      {/* ── Header ── */}
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
      >
        <Modal.Title
          className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
          id="modal_title"
          style={{ fontSize: "14px", fontWeight: "600" }}
        >
          {isVideo ? "Video Recording" : "Audio Recording"}
        </Modal.Title>
      </Modal.Header>

      {/* ── Body ── */}
      <Modal.Body style={{ padding: "5px", minHeight: "100px" }}>
        {/* No recording passed */}
        {!selectedRecording && (
          <div style={{ textAlign: "center", color: "#6b7f95", fontSize: 13, padding: "20px 0" }}>
            No recording selected.
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              background: "#fff5f5",
              border: "1px solid #f5c6cb",
              borderRadius: 6,
              padding: "12px 14px",
            }}
          >
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#c0392b" }}>{error}</p>
          </div>
        )}

        {deleteError && (
          <div
            style={{
              background: "#fff5f5",
              border: "1px solid #f5c6cb",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 5,
            }}
          >
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#c0392b" }}>
              {deleteError}
            </p>
          </div>
        )}

        {descriptionError && (
          <div
            style={{
              background: "#fff5f5",
              border: "1px solid #f5c6cb",
              borderRadius: 6,
              padding: "12px 14px",
              marginBottom: 5,
            }}
          >
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#c0392b" }}>
              {descriptionError}
            </p>
          </div>
        )}

        {/* ── Video player ── */}
        {!error && selectedRecording && isVideo && (
          <div className="rp-player">
            <video
              ref={setVideoRef}
              src={fileUrl}
              style={{
                width: "100%",
                borderRadius: 6,
                background: "#000",
                maxHeight: 280,
                display: "block",
              }}
              preload="metadata"
              onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            <PlayerControls
              currentTime={currentTime}
              duration={duration}
              progress={progress}
              playing={playing}
              playbackRate={playbackRate}
              rates={rates}
              onSeek={handleSeekChange}
              onTogglePlay={togglePlay}
              onRestart={restart}
              onSkip={skip}
              onRateChange={handleRateChange}
            />
          </div>
        )}

        {/* ── Audio player ── */}
        {!error && selectedRecording && !isVideo && (
          <div className="rp-player">
            {/* Waveform placeholder */}
            <div
              style={{
                height: 60,
                background: "#f0f4f8",
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                padding: "0 12px",
              }}
            >
              {Array.from({ length: 40 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: 3,
                    borderRadius: 2,
                    background: i / 40 < progress / 100 ? "#1a4f8a" : "#c5d0dc",
                    height: `${20 + Math.sin(i * 0.7) * 14 + Math.sin(i * 1.3) * 8}px`,
                    transition: "background 0.1s",
                  }}
                />
              ))}
            </div>
            <PlayerControls
              currentTime={currentTime}
              duration={duration}
              progress={progress}
              playing={playing}
              playbackRate={playbackRate}
              rates={rates}
              onSeek={handleSeekChange}
              onTogglePlay={togglePlay}
              onRestart={restart}
              onSkip={skip}
              onRateChange={handleRateChange}
            />
          </div>
        )}

        {selectedRecording && (
          <div className="mt-2">
            <label htmlFor="recording-description" className="form-label mb-1 fw-semibold">
              Description
            </label>
            <textarea
              id="recording-description"
              className="form-control"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={deleting || savingDescription}
              placeholder="Add a description for this recording"
            />
          </div>
        )}
      </Modal.Body>

      {/* ── Footer ── */}
      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider d-flex align-items-center justify-content-center"
        style={{ borderTop: "none" }}
      >
        <Button
          variant="secondary"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleClose}
          disabled={deleting || savingDescription}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          Close
        </Button>
        <Button
          variant="danger"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={onDelete}
          disabled={!selectedRecording?.id || deleting || savingDescription}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          {deleting ? "Deleting..." : "Delete"}
        </Button>
        <Button
          variant="primary"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleSaveDescription}
          disabled={!selectedRecording?.id || deleting || savingDescription}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          {savingDescription ? "Saving..." : "Save"}
        </Button>
        <Button
          variant="primary"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleDownload}
          disabled={!fileUrl || deleting || savingDescription}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          Download
        </Button>
      </Modal.Footer>

      <style>{`
        .rp-player { display: flex; flex-direction: column; gap: 5px; }

        .rp-seek-row { display: flex; align-items: center; gap: 10px; }
        .rp-time-lbl {
          font-size: 12px; font-weight: 600; color: #4a5568;
          min-width: 36px; white-space: nowrap; font-variant-numeric: tabular-nums;
        }
        .rp-track-wrap {
          flex: 1; position: relative; height: 20px;
          display: flex; align-items: center; cursor: pointer;
        }
        .rp-track-bg, .rp-track-fill {
          position: absolute; left: 0; height: 5px; border-radius: 4px; pointer-events: none;
        }
        .rp-track-bg   { width: 100%; background: #dde3ea; }
        .rp-track-fill { background: #1a4f8a; }
        .rp-thumb {
          position: absolute; width: 14px; height: 14px;
          background: #1a4f8a; border: 2px solid #fff;
          border-radius: 50%; box-shadow: 0 1px 4px rgba(0,0,0,0.22);
          pointer-events: none; transform: translateX(-50%);
        }
        .rp-range {
          position: absolute; inset: 0; width: 100%; height: 100%;
          opacity: 0; cursor: pointer; margin: 0; padding: 0;
        }
        .rp-track-wrap:hover .rp-track-fill,
        .rp-track-wrap:hover .rp-thumb { background: #154080; }

        .rp-controls {
          display: flex; align-items: center; justify-content: center;
          gap: 8px; flex-wrap: wrap;
        }
        .rp-ctrl {
          background: #f0f4f8; border: 1px solid #d5dde8; border-radius: 50%;
          width: 34px; height: 34px;
          display: flex; align-items: center; justify-content: center;
          flex-direction: column; color: #1a4f8a; cursor: pointer;
          padding: 0; gap: 1px; transition: background 0.15s; flex-shrink: 0;
        }
        .rp-ctrl:hover { background: #dce6f0; }
        .rp-skip-num { font-size: 8px; font-weight: 700; color: #1a4f8a; line-height: 1; }
        .rp-play-btn {
          background: #1a4f8a; border: none; border-radius: 50%;
          width: 46px; height: 46px;
          display: flex; align-items: center; justify-content: center;
          color: #fff; cursor: pointer; flex-shrink: 0;
          box-shadow: 0 3px 10px rgba(26,79,138,0.35);
          transition: background 0.15s, transform 0.1s;
        }
        .rp-play-btn:hover  { background: #154080; transform: scale(1.05); }
        .rp-play-btn:active { transform: scale(0.97); }

        .rp-rates {
          display: flex; gap: 3px;
          background: #f0f4f8; border: 1px solid #d5dde8;
          border-radius: 20px; padding: 3px;
        }
        .rp-rate {
          background: transparent; border: none; border-radius: 14px;
          padding: 2px 7px; font-size: 11px; font-weight: 700;
          color: #6b7f95; cursor: pointer;
          transition: background 0.15s, color 0.15s; white-space: nowrap;
        }
        .rp-rate:hover { color: black; }
        .rp-rate--on   { background: #1a4f8a; color: #fff; }

        @media (max-width: 480px) {
          .rp-controls { gap: 6px; }
          .rp-play-btn { width: 40px; height: 40px; }
          .rp-ctrl     { width: 30px; height: 30px; }
          .rp-rate     { padding: 2px 5px; font-size: 10px; }
        }
      `}</style>
    </Modal>
  );
};

// ── Shared controls strip ──────────────────────────────────────────────────────
const PlayerControls = ({
  currentTime,
  duration,
  progress,
  playing,
  playbackRate,
  rates,
  onSeek,
  onTogglePlay,
  onRestart,
  onSkip,
  onRateChange,
}) => (
  <>
    <div className="rp-seek-row">
      <span className="rp-time-lbl text-end">{fmt(currentTime)}</span>
      <div className="rp-track-wrap">
        <div className="rp-track-bg" />
        <div className="rp-track-fill" style={{ width: `${progress}%` }} />
        <div className="rp-thumb" style={{ left: `${progress}%` }} />
        <input
          type="range"
          className="rp-range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={onSeek}
        />
      </div>
      <span className="rp-time-lbl text-start">{fmt(duration)}</span>
    </div>

    <div className="rp-controls">
      <button className="rp-ctrl" onClick={onRestart} title="Restart">
        <IconRestart />
      </button>
      <button className="rp-ctrl rp-ctrl--skip" onClick={() => onSkip(-10)} title="Rewind 10s">
        <IconRewind />
        <span className="rp-skip-num">10</span>
      </button>
      <button className="rp-play-btn" onClick={onTogglePlay}>
        {playing ? <IconPause /> : <IconPlay />}
      </button>
      <button className="rp-ctrl rp-ctrl--skip" onClick={() => onSkip(10)} title="Forward 10s">
        <IconFastForward />
        <span className="rp-skip-num">10</span>
      </button>
      <div className="rp-rates">
        {rates.map((r) => (
          <button
            key={r}
            className={`rp-rate ${playbackRate === r ? "rp-rate--on" : ""}`}
            onClick={() => onRateChange(r)}
          >
            {r}×
          </button>
        ))}
      </div>
    </div>
  </>
);

// Icons inside PlayerControls — need to be in scope
// const IconPlay2 = IconPlay;
// const IconPause2 = IconPause;
// const IconRestart2 = IconRestart;
// const IconRewind2 = IconRewind;
// const IconFF2 = IconFastForward;

export default VideoAudioPlayer;
